import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../bom/ui/Toast';
import { sbomApi } from './sbomApi';
import useStickyHead from './useStickyHead';
import { AssessDrawer } from './SbomAssess';
import SbomCheckHistory, { SaveCheckForm } from './SbomCheckHistory';
import SbomVulnOrcanos from './SbomVulnOrcanos';

const TILES = [
  ['all', 'All findings', ''],
  ['unassessed', 'Need assessment', 'warn'],
  ['open', 'Affected, not controlled', 'bad'],
  ['controlled', 'Affected, controlled', ''],
  ['not_affected', 'Not affected', 'ok'],
  ['kev', 'Actively exploited (KEV)', 'crit'],
  ['critical', 'Critical or high', ''],
];
const CONTROL_TONE = { controlled: 'ok', accepted: 'ok', mitigated: 'warn', not_run: 'warn', unverified: 'warn', failed: 'bad', uncontrolled: 'bad' };
const CONTROL_LABEL = { controlled: 'Controlled', accepted: 'Accepted', mitigated: 'Mitigated', not_run: 'Not run',
  unverified: 'Unverified', failed: 'Failed', uncontrolled: 'Uncontrolled', missing: 'Not in panel' };

/** A running check: step n of 4, the step's `done of total` when it counts, and one bar for the whole check. */
function ScanProgress({ scan }) {
  const p = scan.progress;
  if (!p) return <span className="sbom-tiny">Checking… {scan.stage}</span>;
  const steps = p.steps || 4;
  const step = Math.min(steps, Math.max(1, p.step || 1));
  const part = p.total ? Math.min(1, (p.done || 0) / p.total) : 0;
  const pct = Math.max(3, Math.round(((step - 1 + part) / steps) * 100));
  return (
    <span className="sbom-scanprog" role="status">
      <span className="sbom-prog" title={`${pct}%`}><i style={{ width: `${pct}%` }} /></span>
      <span className="sbom-tiny">Step {step} of {steps} · {scan.stage || 'Checking…'}
        {p.total ? ` · ${p.done || 0} of ${p.total}` : ''}</span>
    </span>
  );
}

/** When the components were last checked — or why they were not. Never reads as "none" when unchecked. */
export function ScanLine({ scan, onCheck, busy }) {
  if (!scan) return null;
  if (!scan.enabled) return <span className="sbom-tiny sbom-muted">Vulnerability checking is off (Settings → Software BOM).</span>;
  const when = scan.ok_at ? `${String(scan.ok_at).slice(0, 16)} UTC` : '';
  return (
    <span className="sbom-scanline">
      {scan.running ? <ScanProgress scan={scan} />
        : scan.error ? <span className="sbom-tiny sbom-badtext" title={scan.error}>
          {when ? `Not checked since ${when}` : 'Not checked yet'} — {scan.error.replace(/^The vulnerability check failed: /, '')}</span>
          : when ? <span className="sbom-scan-t sbom-muted">Checked {when} · OSV.dev{scan.kev_enabled ? ' + CISA KEV' : ''}{scan.daily ? ' · daily' : ''}</span>
            : <span className="sbom-tiny sbom-muted">Not checked yet</span>}
      {onCheck && <button type="button" className="sbom-btn sbom-btn--ghost" onClick={onCheck} disabled={busy || scan.running}>
        {scan.running ? 'Checking…' : 'Check now'}</button>}
    </span>
  );
}

/** Polls the scan while it runs; returns [scan, start, setScan]. */
export function useScan(onAuth, onDone) {
  const [scan, setScan] = useState(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const running = !!scan?.running;
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => {
      sbomApi.vulnStatus().then((st) => {
        setScan(st);
        if (!st.running) doneRef.current?.();
      }).catch((e) => onAuth(e));
    }, 1000);
    return () => clearInterval(t);
  }, [running, onAuth]);
  const start = useCallback(() => sbomApi.scan().then(setScan).catch((e) => { if (!onAuth(e)) throw e; }), [onAuth]);
  return [scan, start, setScan];
}

/** Banner over a past check: what it is, how it differs from the one before, Save, and the way back. */
function CheckBanner({ check, onBack, onSaved }) {
  const [saving, setSaving] = useState(false);
  const gone = check.gone || [];
  return (
    <div className="sbom-checkbanner" role="status">
      <div className="sbom-checkbanner-row">
        <strong>Past check · {String(check.finished_at || '').slice(0, 16)} UTC</strong>
        {check.saved && <span className="sbom-pill sbom-pill--plain">★ {check.saved_name}</span>}
        {check.latest && <span className="sbom-pill sbom-pill--info">Latest check</span>}
        <span className="sbom-tiny sbom-muted">
          Read-only: the findings and verdicts as they stood then.
          {check.previous ? ` Compared with the check of ${String(check.previous.finished_at || '').slice(0, 16)} UTC:`
            + ` ${check.stats?.new || 0} new, ${gone.length} gone.` : ' The first check in the history.'}
        </span>
        <span style={{ flex: 1 }} />
        {!saving && <button type="button" className="sbom-btn sbom-btn--ghost" onClick={() => setSaving(true)}>{check.saved ? 'Rename' : 'Save this check'}</button>}
        <button type="button" className="sbom-btn" onClick={onBack}>Back to current</button>
      </div>
      {check.saved_note && <div className="sbom-tiny">{check.saved_note}</div>}
      {saving && <SaveCheckForm check={check} onCancel={() => setSaving(false)} onSaved={() => { setSaving(false); onSaved(); }} />}
      {gone.length > 0 && (
        <details>
          <summary className="sbom-tiny">{gone.length} no longer found in this check</summary>
          <ul className="sbom-tiny sbom-gone">
            {gone.map((g) => <li key={`${g.vuln_id}|${g.package_key}`}><span className="sbom-mono">{g.vuln_id}</span> · {g.package}
              {' '}<span className={`sbom-chip sbom-chip--${g.severity}`}>{g.severity}</span></li>)}
          </ul>
        </details>
      )}
    </div>
  );
}

/**
 * Vulnerabilities — every finding across releases, one row per vulnerability × package, filtered on the
 * server (Critical Note #38). Assess opens the drawer: a VEX verdict, and for Affected the risk it creates.
 * `?check=<id>` shows a past check from the history instead, read-only (no Assess, no Check now).
 * `?locate=<vuln id>|<package key>` (the SBOM URL on an Orcanos Vulnerability item) searches for it and
 * highlights its row; clicking the row, a new search or another view clears it (locate = highlight).
 */
export default function SbomVulns({ info, onAuth }) {
  const headBox = useStickyHead();
  const { showToast } = useToast();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') || 'all';
  const release = params.get('release') || '';
  const checkId = params.get('check') || '';
  const [history, setHistory] = useState(false);
  const locate = params.get('locate') || '';
  const [q, setQ] = useState(() => locate.split('|')[0] || '');
  const [writing, setWriting] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [releases, setReleases] = useState([]);
  const [open, setOpen] = useState(null);       // {vuln_id, package_key}
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  const [scan, startScan, setScan] = useScan(onAuth, reload);

  useEffect(() => {
    let off = false;
    const t = setTimeout(() => {
      const req = checkId ? sbomApi.check(checkId, { view, q: q.trim(), release }) : sbomApi.findings({ view, q: q.trim(), release });
      req.then((d) => { if (!off) { setData(d); if (d.scan) setScan(d.scan); setError(''); } })
        .catch((e) => { if (!off && !onAuth(e)) { setData(null); setError(e.message); } });
    }, q ? 250 : 0);
    return () => { off = true; clearTimeout(t); };
  }, [view, q, release, checkId, tick, onAuth, setScan]);

  useEffect(() => {
    if (checkId && !scan) sbomApi.vulnStatus().then(setScan).catch(() => {});
  }, [checkId, scan, setScan]);

  useEffect(() => {
    sbomApi.releases().then((d) => setReleases((d.releases || []).filter((r) => r.imported))).catch(() => {});
  }, []);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v && v !== 'all') next.set(k, v); else next.delete(k);
    if (k !== 'locate') next.delete('locate');
    setParams(next, { replace: true });
  };
  const unlocate = () => setParam('locate', '');
  // The located row is brought into view once it is drawn (Note #113: scrollIntoView, never window.scrollTo).
  const located = !!(locate && data?.items?.some((x) => `${x.vuln_id}|${x.package_key}` === locate));
  useEffect(() => {
    if (located) document.querySelector('.sbom-row--located')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [located]);
  const openCheck = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set('check', String(id)); else next.delete('check');
    next.delete('release');
    setHistory(false);
    setData(null);
    setParams(next);
  };
  const past = checkId ? data?.check : null;
  const relOptions = checkId ? (data?.releases || []) : releases;

  const check = () => startScan().catch((e) => showToast(e.message, 'error'));

  return (
    <>
      <div className="sbom-head">
        <div>
          <h2>Vulnerabilities</h2>
          <div className="sbom-sub">Known vulnerabilities in every release, checked by package and version. Only package names
            and versions leave the server, never your code.</div>
        </div>
        <div className="sbom-sp" />
        <span className="sbom-scanline">
          <ScanLine scan={scan} onCheck={checkId ? null : check} />
          <button type="button" className="sbom-btn sbom-btn--ghost" onClick={() => setHistory(true)}>History</button>
          {!checkId && data?.vuln_item && (
            <button type="button" className="sbom-btn sbom-btn--ghost" onClick={() => setWriting(true)}
              title="Choose which vulnerabilities to write to Orcanos as Vulnerability items">
              Write to Orcanos{data.orcanos_due > 0 && <span className="sbom-orc-due" title={`${data.orcanos_due} to write or update by the saved rule`}>{data.orcanos_due}</span>}
            </button>
          )}
        </span>
      </div>
      {past && <CheckBanner check={past} onBack={() => openCheck('')} onSaved={reload} />}
      {error && <div className="sbom-error" role="alert">{error}
        {checkId && <> <button type="button" className="sbom-btn sbom-btn--ghost" onClick={() => openCheck('')}>Back to current</button></>}</div>}
      {data?.risk_note && <div className="sbom-warn" style={{ marginBottom: 12 }}>{data.risk_note}</div>}
      {data && data.not_checkable > 0 && (
        <div className="sbom-note" style={{ marginBottom: 12 }}>
          {data.not_checkable} of {data.components} components cannot be checked: they have no package URL with a version, or one
          OSV.dev does not cover (for example <span className="sbom-mono">pkg:generic</span>). They show <i>Not checked</i>, never <i>none</i>.
        </div>
      )}

      <div className="sbom-tiles">
        {TILES.map(([k, l, tone]) => (
          <button type="button" key={k} className={`sbom-tile${view === k ? ' sbom-tile--on' : ''}`} onClick={() => setParam('view', k)}
            aria-pressed={view === k}>
            <span className={`sbom-tile-n${tone ? ` sbom-tile-n--${tone}` : ''}`}>{data ? data.counts[k] : '…'}</span>
            <span className="sbom-tile-l">{l}</span>
          </button>
        ))}
      </div>

      <div className="sbom-table-card">
        <div className="sbom-tbar">
          <input type="search" className="sbom-search" placeholder="Find a CVE or package" value={q} aria-label="Search vulnerabilities"
            onChange={(e) => { setQ(e.target.value); if (locate) unlocate(); }} />
          <select className="sbom-select" style={{ width: 'auto' }} value={release} aria-label="Release"
            onChange={(e) => setParam('release', e.target.value)}>
            <option value="">All releases</option>
            {relOptions.map((r) => <option key={r.item_id} value={r.item_id}>{r.name}</option>)}
          </select>
          <span style={{ flex: 1 }} />
          {data && <span className="sbom-tiny sbom-muted">{data.total} finding{data.total === 1 ? '' : 's'}</span>}
        </div>
        <div className="sbom-tw sbom-tw--sticky" ref={headBox}>
          <table className="sbom-table">
            <thead>
              <tr><th>Vulnerability</th><th>Package</th><th>Severity</th><th className="sbom-hide-s">Releases</th>
                <th>Assessment</th><th>Risk control</th>{data?.vuln_item && <th className="sbom-hide-s">Orcanos</th>}
                <th className="sbom-hide-s">First seen</th><th /></tr>
            </thead>
            <tbody>
              {!data && !error && <tr><td colSpan={9} className="sbom-empty">Loading…</td></tr>}
              {data && data.items.length === 0 && (
                <tr><td colSpan={9} className="sbom-empty">
                  {data.counts.all === 0
                    ? (checkId ? 'This check found no known vulnerabilities.' : scan?.ok_at ? 'No known vulnerabilities in any release.' : 'Not checked yet. Press Check now.')
                    : 'Nothing matches this filter.'}
                </td></tr>
              )}
              {data && data.items.map((x) => {
                const a = x.assessment || {};
                const todo = x.verdict.rank === 4;
                return (
                  <tr key={`${x.vuln_id}|${x.package_key}`}
                    className={locate === `${x.vuln_id}|${x.package_key}` ? 'sbom-row--located' : undefined}
                    onClick={locate === `${x.vuln_id}|${x.package_key}` ? unlocate : undefined}>
                    <td className="sbom-wrap">
                      <span className="sbom-mono">{x.vuln_id}</span>{(checkId ? x.new_in_check : x.new) && <span className="sbom-pill sbom-pill--info" style={{ marginLeft: 6 }}>New</span>}
                      <div className="sbom-tiny sbom-muted sbom-clamp">{x.summary}</div>
                    </td>
                    <td className="sbom-wrap-pkg">{x.package} <span className="sbom-mono sbom-muted">{x.versions.map((v) => v.version).join(', ')}</span></td>
                    <td>
                      <span className={`sbom-chip sbom-chip--${x.severity}`}>{x.severity}{x.score != null && ` ${x.score}`}</span>
                      {x.kev && <span className="sbom-kev" title={`On the CISA KEV list since ${x.kev}`}>KEV</span>}
                    </td>
                    <td className="sbom-hide-s sbom-rels">
                      {x.releases.map((r) => <span key={r.item_id} className="sbom-pill sbom-pill--plain sbom-rel-pill">{r.name}</span>)}
                      {x.fixed_in.length > 0 && <div className="sbom-rels-fixed"><span className="sbom-pill sbom-pill--ok sbom-rel-pill">not in {x.fixed_in.map((r) => r.name).join(', ')}</span></div>}
                    </td>
                    <td><span className={`sbom-pill sbom-pill--${x.verdict.tone}`}>{x.verdict.label}</span></td>
                    <td>
                      {a.risk_key ? (
                        <><span className="sbom-mono">{a.risk_key}</span>{' '}
                          <span className={`sbom-pill sbom-pill--${CONTROL_TONE[a.risk_control] || 'plain'}`}>{CONTROL_LABEL[a.risk_control] || 'Not read'}</span></>
                      ) : a.mitigation_key ? <span className="sbom-tiny"><span className="sbom-muted">via</span> <span className="sbom-mono">{a.mitigation_key}</span></span>
                        : <span className="sbom-muted">—</span>}
                    </td>
                    {data.vuln_item && (
                      <td className="sbom-hide-s">
                        {x.orcanos ? <><span className="sbom-mono">{x.orcanos.key}</span>
                          {x.orcanos.changed && <span className="sbom-pill sbom-pill--warn" style={{ marginLeft: 4 }}
                            title="The vulnerability or its assessment changed since it was written. Write to Orcanos updates it.">Changed</span>}</>
                          : <span className="sbom-muted">—</span>}
                      </td>
                    )}
                    <td className="sbom-hide-s sbom-mono">{String(x.first_seen || '').slice(0, 10)}</td>
                    <td>{checkId ? null : <button type="button" className={`sbom-btn${todo ? ' sbom-btn--pri' : ''}`}
                      onClick={() => setOpen({ vuln_id: x.vuln_id, package_key: x.package_key })}>{todo ? 'Assess' : 'Open'}</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {writing && <SbomVulnOrcanos releases={relOptions} onClose={() => setWriting(false)} onDone={reload} onAuth={onAuth} />}
      {history && <SbomCheckHistory current={checkId} onOpen={openCheck} onClose={() => setHistory(false)} onAuth={onAuth} />}
      {open && (
        <AssessDrawer vulnId={open.vuln_id} pkg={open.package_key} info={info} onAuth={onAuth}
          onClose={() => setOpen(null)} onSaved={reload} />
      )}
    </>
  );
}
